/**
 * The one place the browser learns which Supabase key to use.
 *
 * A live service_role key sits in 92 commits on origin/main and still
 * authenticates. Killing it means disabling this project's legacy JWT API keys —
 * and the moment that happens, anything still presenting the legacy anon key
 * stops working. This module makes that impossible for the front end.
 *
 * A PUBLISHABLE KEY IS PUBLIC BY DESIGN. It is shipped in the bundle, it is
 * visible in the network tab, and it grants nothing beyond what row-level
 * security already allows an anonymous visitor. So the value is written here as a
 * literal rather than left to a build-time variable we cannot see from here.
 *
 * The environment is not consulted at all — see the note further down. The
 * short version: `VITE_SUPABASE_PUBLISHABLE_KEY` in Vercel holds the LEGACY
 * anon JWT under a publishable-sounding name, and a runtime guard against it
 * was not enough, because Vite inlines what it reads.
 */

/** The project's publishable key (new scheme). Public, by design. */
const PUBLISHABLE_LITERAL = "sb_publishable_yVc7B6B0vueTzoY_ifNboQ_9XG3Ef2v";

// The key is NOT read from the environment, and that is deliberate.
//
// Reading it back was worse than useless: `VITE_SUPABASE_PUBLISHABLE_KEY` and
// `VITE_SUPABASE_ANON_KEY` in Vercel both still hold the LEGACY anon JWT, which
// Supabase disabled on 2026-07-30 — presenting it now returns 401 "Legacy API
// keys are disabled". The guard below rejected those values at runtime, but
// Vite inlines `import.meta.env` at build time, so merely mentioning them
// stamped the dead JWT into the shipped bundle twice. A dead credential in a
// public bundle is a trap for the next reader, who will reasonably assume the
// app uses what the app ships.
//
// So the literal above is the only source. Rotating the key means editing this
// line — which is the same edit either way, since a new key would have to be
// put in Vercel by hand too.

export const SUPABASE_URL: string = "https://wnzxffwiiubqvcvnnjbz.supabase.co";

export const SUPABASE_PUBLISHABLE_KEY: string = PUBLISHABLE_LITERAL;

/**
 * Kept as an alias so the many call sites that used to read
 * `import.meta.env.VITE_SUPABASE_ANON_KEY` directly can point here without each
 * one having to reason about which scheme it is getting.
 */
export const SUPABASE_ANON_KEY = SUPABASE_PUBLISHABLE_KEY;

/** True when the key in use is the new scheme. Used by the key self-check. */
export const ON_NEW_KEY_SCHEME = SUPABASE_PUBLISHABLE_KEY.startsWith("sb_publishable_");
